import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Eye, ArrowRight, ArrowsClockwise, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../lib/api';

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

function rowFill(s) {
  if (s === 'success') return 'bg-green-50';
  if (s === 'pending') return 'bg-amber-50';
  if (s === 'failed') return 'bg-red-50';
  return '';
}

export default function MyTeamReportPage() {
  const [data, setData] = useState({ sub_staff: [], grand_total: { total: 0, success: 0, pending: 0, failed: 0, amount: 0, submitted: 0, approved: 0 } });
  const [loading, setLoading] = useState(true);
  const [drilling, setDrilling] = useState({}); // sub_id -> rows
  const [drillFilter, setDrillFilter] = useState({}); // sub_id -> status
  const [expanded, setExpanded] = useState(new Set());
  const [approving, setApproving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/sub-bookings/team');
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fetchDrill = async (subId, status = 'all') => {
    try {
      const url = `/sub-bookings/team/${subId}` + (status && status !== 'all' ? `?status=${status}` : '');
      const { data: d } = await api.get(url);
      setDrilling(prev => ({ ...prev, [subId]: d.rows || [] }));
    } catch (err) {
      toast.error('Drill-down failed');
    }
  };

  const toggle = async (subId) => {
    const next = new Set(expanded);
    if (next.has(subId)) {
      next.delete(subId);
    } else {
      next.add(subId);
      if (!drilling[subId]) await fetchDrill(subId, drillFilter[subId] || 'all');
    }
    setExpanded(next);
  };

  const setFilter = async (subId, status) => {
    setDrillFilter(prev => ({ ...prev, [subId]: status }));
    await fetchDrill(subId, status);
  };

  const approveAll = async () => {
    if (!window.confirm(`${data.grand_total.submitted} submitted rows admin ko forward ho jayengi. Confirm?`)) return;
    setApproving(true);
    try {
      const { data: r } = await api.post('/sub-bookings/approve-all');
      toast.success(`${r.approved} rows approve ho gaye → admin ke pas`);
      await load();
      setExpanded(new Set());
      setDrilling({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approve failed');
    } finally { setApproving(false); }
  };

  const reject = async (rowId, subId) => {
    try {
      await api.post(`/sub-bookings/reject/${rowId}`);
      toast.success('Row sub-staff ke draft me wapas chala gaya');
      await fetchDrill(subId, drillFilter[subId] || 'all');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reject failed');
    }
  };

  const grand = data.grand_total;

  return (
    <div className="space-y-4" data-testid="my-team-report">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold">My Team Bookings</h1>
          <p className="text-sm text-gray-500">Aapke under ke sab sub-staff ka rolled-up summary. Drill-down karke detail dekho.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { l: 'Team Bookings', v: grand.total, c: 'indigo' },
          { l: 'Submitted', v: grand.submitted, c: 'blue' },
          { l: 'Approved', v: grand.approved, c: 'emerald' },
          { l: 'Pending', v: grand.pending, c: 'amber' },
          { l: 'Total Amount', v: fmt(grand.amount), c: 'fuchsia' },
        ].map(s => (
          <div key={s.l} className={`bg-${s.c}-50 border border-${s.c}-200 rounded p-3`}>
            <div className={`text-[10px] uppercase text-${s.c}-700 font-bold`}>{s.l}</div>
            <div className="text-2xl font-bold">{s.v}</div>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto bg-white border rounded">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Sub-Staff</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">Success 🟢</th>
              <th className="px-3 py-2 text-right">Pending 🟡</th>
              <th className="px-3 py-2 text-right">Failed 🔴</th>
              <th className="px-3 py-2 text-right">Submitted</th>
              <th className="px-3 py-2 text-right">Approved</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center text-gray-400 py-8">Loading…</td></tr>
            ) : data.sub_staff.length === 0 ? (
              <tr><td colSpan={9} className="text-center text-gray-400 py-8">Aapke under koi sub-staff nahi hai. Admin se add karwao.</td></tr>
            ) : (
              <>
                {data.sub_staff.map(s => (
                  <>
                  <tr key={s.id} className="border-t hover:bg-gray-50" data-testid={`sub-row-${s.id}`}>
                    <td className="px-3 py-2 font-medium">
                      {s.name}
                      <div className="text-[10px] text-gray-500">{s.email}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold">{s.total}</td>
                    <td className="px-3 py-2 text-right text-green-700">{s.success}</td>
                    <td className="px-3 py-2 text-right text-amber-700">{s.pending}</td>
                    <td className="px-3 py-2 text-right text-red-700">{s.failed}</td>
                    <td className="px-3 py-2 text-right text-blue-700">{s.submitted}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{s.approved}</td>
                    <td className="px-3 py-2 text-right">{fmt(s.amount)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => toggle(s.id)} data-testid={`drill-${s.id}`}>
                        <Eye size={12} /> {expanded.has(s.id) ? 'Hide' : 'View'}
                      </Button>
                    </td>
                  </tr>
                  {expanded.has(s.id) && (
                    <tr className="bg-gray-50">
                      <td colSpan={9} className="px-3 py-3">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {['all', 'success', 'pending', 'failed'].map(st => (
                            <button key={st} onClick={() => setFilter(s.id, st)} className={`text-[10px] px-2 py-0.5 rounded border ${(drillFilter[s.id] || 'all') === st ? 'bg-blue-600 text-white border-blue-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}>
                              {st.toUpperCase()}
                            </button>
                          ))}
                        </div>
                        <div className="overflow-x-auto bg-white border rounded">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-100 text-[10px] uppercase text-gray-500">
                              <tr>
                                <th className="px-2 py-1 text-left">Card</th>
                                <th className="px-2 py-1 text-left">Consumer ID</th>
                                <th className="px-2 py-1 text-right">Amount</th>
                                <th className="px-2 py-1 text-left">Status</th>
                                <th className="px-2 py-1 text-left">Phase</th>
                                <th className="px-2 py-1 text-left">Submitted</th>
                                <th className="px-2 py-1 text-left">Remarks</th>
                                <th className="px-2 py-1 text-center w-12"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {(drilling[s.id] || []).length === 0 ? (
                                <tr><td colSpan={8} className="text-center text-gray-400 py-3">No rows</td></tr>
                              ) : drilling[s.id].map(r => (
                                <tr key={r.id} className={`border-t ${rowFill(r.status)}`}>
                                  <td className="px-2 py-1 font-mono">****{r.card_last4 || '----'}</td>
                                  <td className="px-2 py-1 font-mono">{r.consumer_id}</td>
                                  <td className="px-2 py-1 text-right">{fmt(r.amount)}</td>
                                  <td className="px-2 py-1 capitalize">{r.status}</td>
                                  <td className="px-2 py-1 capitalize">
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${r.phase_status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>{r.phase_status}</span>
                                  </td>
                                  <td className="px-2 py-1 text-[10px] text-gray-500">{r.submitted_at ? new Date(r.submitted_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                                  <td className="px-2 py-1">{r.remarks || '-'}</td>
                                  <td className="px-2 py-1 text-center">
                                    {r.phase_status === 'submitted' && (
                                      <button onClick={() => reject(r.id, s.id)} className="text-red-500 hover:text-red-700" title="Reject — sub-staff ko draft me wapas bhej do">
                                        <X size={12} />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                  </>
                ))}
                <tr className="border-t bg-gray-100 font-bold">
                  <td className="px-3 py-2">GRAND TOTAL</td>
                  <td className="px-3 py-2 text-right">{grand.total}</td>
                  <td className="px-3 py-2 text-right text-green-700">{grand.success}</td>
                  <td className="px-3 py-2 text-right text-amber-700">{grand.pending}</td>
                  <td className="px-3 py-2 text-right text-red-700">{grand.failed}</td>
                  <td className="px-3 py-2 text-right text-blue-700">{grand.submitted}</td>
                  <td className="px-3 py-2 text-right text-emerald-700">{grand.approved}</td>
                  <td className="px-3 py-2 text-right">{fmt(grand.amount)}</td>
                  <td></td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button onClick={load} variant="outline" className="gap-1"><ArrowsClockwise size={16} /> Refresh</Button>
        <Button
          onClick={async () => {
            try {
              const r = await api.get('/sub-bookings/team-export', { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([r.data]));
              const a = document.createElement('a');
              a.href = url;
              a.download = `Team_Bookings_${new Date().toISOString().slice(0, 10)}.xlsx`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
              toast.success('Excel downloaded');
            } catch (err) {
              toast.error('Export failed');
            }
          }}
          variant="outline"
          className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          data-testid="export-excel-btn"
        >
          <ArrowRight size={16} className="rotate-90" /> Export Excel
        </Button>
        <Button onClick={approveAll} disabled={approving || (grand.submitted || 0) === 0} className="bg-blue-600 hover:bg-blue-700 gap-1 ml-auto" data-testid="approve-all-btn">
          <ArrowRight size={16} /> {approving ? 'Approving…' : `Approve All Submitted (${grand.submitted || 0}) → Admin`}
        </Button>
      </div>
    </div>
  );
}
